import { useEffect, useState, useRef } from "react";
import { Layout } from "../components/Layout";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import { UploadCloud, FileText, Image as ImageIcon, Download, Trash2, Search, Folder, Eye, X } from "lucide-react";

const CATEGORIES = ["Contratos CVP", "Minutas", "Certificados Energéticos", "Licenças de Utilização", "Documentos Pessoais"];

function fmtSize(b) {
  if (!b) return "—";
  if (b < 1024) return b + " B";
  if (b < 1048576) return (b / 1024).toFixed(1) + " KB";
  return (b / 1048576).toFixed(1) + " MB";
}

export default function Documentos() {
  const [docs, setDocs] = useState([]);
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = () => api.get("/documents").then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      try { await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); }
      catch { toast.error(`Erro ao carregar ${file.name}`); }
    }
    setUploading(false); toast.success("Documento(s) carregado(s)"); load();
  };

  const remove = async (id) => { if (!window.confirm("Eliminar documento?")) return; await api.delete(`/documents/${id}`); toast.success("Eliminado"); load(); };

  const urlFor = (d) => `${API}/documents/${d.id}/download?auth=${localStorage.getItem("almar_token")}`;
  const download = (d) => { window.open(urlFor(d), "_blank"); };
  const canPreview = (d) => (d.content_type || "").startsWith("image") || (d.content_type || "").includes("pdf");

  const filtered = docs.filter((d) =>
    (!filter || d.category === filter) &&
    (!q || d.original_filename.toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <Layout title="Gestão de Documentos">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-4">
          <div className="card-almar p-5">
            <label className="text-xs uppercase tracking-wider text-[#C9A84C] font-medium">Categoria</label>
            <select className="input-almar mt-2" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="doc-category-select">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <div
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files); }}
              onClick={() => inputRef.current?.click()}
              data-testid="upload-dropzone"
              className={`mt-4 border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${drag ? "border-[#C9A84C] bg-[#C9A84C]/10" : "border-[#1E3A56] hover:border-[#C9A84C]/50"}`}>
              <UploadCloud size={34} className="mx-auto text-[#C9A84C] mb-2" />
              <p className="text-sm text-slate-300">{uploading ? "A carregar..." : "Arraste ficheiros ou clique"}</p>
              <p className="text-xs text-slate-500 mt-1">PDF, imagens, documentos</p>
              <input ref={inputRef} type="file" multiple hidden data-testid="file-input"
                onChange={(e) => upload(e.target.files)} />
            </div>
          </div>
          <div className="card-almar p-5">
            <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">Filtrar por categoria</p>
            <button onClick={() => setFilter("")} className={`w-full text-left px-3 py-2 rounded-lg text-sm mb-1 ${!filter ? "bg-[#C9A84C]/15 text-[#E0C574]" : "text-slate-400 hover:bg-[#152A3E]"}`}>Todas</button>
            {CATEGORIES.map((c) => (
              <button key={c} onClick={() => setFilter(c)} className={`w-full text-left px-3 py-2 rounded-lg text-sm mb-1 flex items-center gap-2 ${filter === c ? "bg-[#C9A84C]/15 text-[#E0C574]" : "text-slate-400 hover:bg-[#152A3E]"}`}>
                <Folder size={14} /> {c}
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="relative mb-4">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input className="input-almar pl-9" placeholder="Pesquisar documentos" value={q} onChange={(e) => setQ(e.target.value)} data-testid="doc-search-input" />
          </div>
          <div className="space-y-2" data-testid="documents-list">
            {filtered.map((d) => {
              const isImg = (d.content_type || "").startsWith("image");
              return (
                <div key={d.id} className="card-almar p-4 flex items-center gap-4">
                  <span className="w-11 h-11 rounded-lg bg-[#C9A84C]/15 flex items-center justify-center text-[#C9A84C]">
                    {isImg ? <ImageIcon size={20} /> : <FileText size={20} />}
                  </span>
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => canPreview(d) ? setPreview(d) : download(d)}>
                    <div className="text-sm font-medium text-slate-100 truncate hover:text-[#E0C574] transition-all">{d.original_filename}</div>
                    <div className="text-xs text-slate-500">{d.category} · {fmtSize(d.size)} · {(d.created_at || "").slice(0, 10)}</div>
                  </div>
                  {canPreview(d) && <button onClick={() => setPreview(d)} data-testid={`preview-doc-${d.id}`} className="text-slate-400 hover:text-[#C9A84C] p-2"><Eye size={17} /></button>}
                  <button onClick={() => download(d)} data-testid={`download-doc-${d.id}`} className="text-slate-400 hover:text-[#C9A84C] p-2"><Download size={17} /></button>
                  <button onClick={() => remove(d.id)} className="text-slate-400 hover:text-red-400 p-2"><Trash2 size={17} /></button>
                </div>
              );
            })}
            {filtered.length === 0 && <p className="text-slate-500 text-sm text-center py-10">Nenhum documento nesta vista.</p>}
          </div>
        </div>
      </div>
      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setPreview(null)} data-testid="preview-modal">
          <div className="bg-[#0D1B2A] border border-[#C9A84C]/40 rounded-xl w-full max-w-4xl h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#1E3A56]">
              <span className="text-sm font-medium text-slate-100 truncate">{preview.original_filename}</span>
              <div className="flex items-center gap-2">
                <button onClick={() => download(preview)} className="text-slate-400 hover:text-[#C9A84C] p-1.5"><Download size={17} /></button>
                <button onClick={() => setPreview(null)} className="text-slate-400 hover:text-red-400 p-1.5"><X size={18} /></button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-[#0A1622] flex items-center justify-center">
              {(preview.content_type || "").startsWith("image")
                ? <img src={urlFor(preview)} alt={preview.original_filename} className="max-w-full max-h-full object-contain" />
                : <iframe title={preview.original_filename} src={urlFor(preview)} className="w-full h-full" />}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
